/**
 * SourcePanel Component
 *
 * Side panel that displays a PDF document with highlighting for a specific source citation.
 * Handles fetching the document, displaying the PDF viewer, and mobile overlay behavior.
 */

import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, X, FileText } from 'lucide-react';
import { PDFViewer } from './PDFViewer';
import api from '../../../services/api';
import type { SourceCitation } from '../../types';

interface SourcePanelProps {
  /** The source citation to display */
  source: SourceCitation | null;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Whether to show as mobile overlay */
  isMobile?: boolean;
}

interface DocumentCache {
  [documentId: string]: {
    url: string;
    title: string;
  };
}

// Simple in-memory cache for downloaded PDFs
const documentCache: DocumentCache = {};

export function SourcePanel({ source, isOpen, onClose, isMobile = false }: SourcePanelProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState<boolean>(false);

  // Fetch document when source changes
  useEffect(() => {
    if (!source || !isOpen) {
      setPdfUrl(null);
      return;
    }

    const fetchDocument = async () => {
      // Check cache first
      if (documentCache[source.document_id]) {
        setPdfUrl(documentCache[source.document_id].url);
        setDocumentTitle(documentCache[source.document_id].title);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/api/intelligence/documents/${source.document_id}/download`, {
          responseType: 'blob',
        });

        // Create blob URL
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        // Cache it
        documentCache[source.document_id] = {
          url,
          title: source.document_title || 'Document'
        };

        setPdfUrl(url);
        setDocumentTitle(source.document_title || 'Document');
      } catch (err: any) {
        console.error('Error fetching document:', err);
        setError(err.response?.data?.error || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [source, isOpen]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      // Note: We keep the cache to avoid re-downloading
      // In a production app, you might want to implement LRU cache with cleanup
    };
  }, []);

  const handleClose = useCallback(() => {
    setFullscreen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Mobile overlay
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col dark:bg-gray-900">
        {/* Mobile header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={20} className="text-primary-600 flex-shrink-0 dark:text-primary-400" />
            <span className="font-medium text-gray-900 truncate dark:text-gray-100">{documentTitle}</span>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:hover:bg-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <Loader2 className="animate-spin text-primary-600 dark:text-primary-400" size={32} />
              <p className="text-gray-600 dark:text-gray-400">Loading document...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-4">
              <AlertCircle className="text-red-500" size={32} />
              <p className="text-red-600 text-center dark:text-red-400">{error}</p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          ) : (
            <PDFViewer
              file={pdfUrl}
              initialPage={source?.page_number || 1}
              highlightText={source?.relevant_excerpt || source?.content_preview}
              title={documentTitle}
            />
          )}
        </div>

        {/* Source info footer */}
        {source && !loading && !error && (
          <div className="px-4 py-2 bg-primary-50 border-t border-primary-200 dark:bg-primary-900/20 dark:border-primary-800">
            <p className="text-xs text-primary-800 dark:text-primary-300">
              {source.page_number && `Page ${source.page_number}`}
              {source.section && ` • ${source.section}`}
              {source.similarity && ` • ${Math.round(source.similarity * 100)}% match`}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Desktop side panel
  return (
    <div
      className={`
        flex flex-col bg-white border-l border-gray-200 h-full dark:bg-gray-900 dark:border-gray-700
        transition-all duration-300 ease-in-out
        ${fullscreen ? 'fixed inset-0 z-50 border-l-0' : 'w-[500px] flex-shrink-0'}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={20} className="text-primary-600 flex-shrink-0 dark:text-primary-400" />
          <span className="font-medium text-gray-900 truncate text-sm dark:text-gray-100">{documentTitle}</span>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 hover:bg-gray-100 rounded transition-colors duration-150 dark:hover:bg-gray-700"
          title="Close panel"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="animate-spin text-primary-600 dark:text-primary-400" size={32} />
            <p className="text-gray-600 dark:text-gray-400">Loading document...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 p-4">
            <AlertCircle className="text-red-500" size={32} />
            <p className="text-red-600 text-center dark:text-red-400">{error}</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        ) : (
          <PDFViewer
            file={pdfUrl}
            initialPage={source?.page_number || 1}
            highlightText={source?.relevant_excerpt || source?.content_preview}
            title={documentTitle}
            fullscreen={fullscreen}
            onToggleFullscreen={() => setFullscreen(!fullscreen)}
          />
        )}
      </div>

      {/* Source info footer */}
      {source && !loading && !error && (
        <div className="px-4 py-2 bg-primary-50 border-t border-primary-200 flex-shrink-0 dark:bg-primary-900/20 dark:border-primary-800">
          <p className="text-xs text-primary-800 dark:text-primary-300">
            {source.page_number && `Page ${source.page_number}`}
            {source.section && ` • ${source.section}`}
            {source.similarity && ` • ${Math.round(source.similarity * 100)}% match`}
          </p>
        </div>
      )}
    </div>
  );
}

export default SourcePanel;
